/**
 * WineListRenderer — moteur de rendu unique de la carte des vins.
 * Partagé par l'aperçu A4 (Composer, `print`) et la carte web publique (fluide).
 * En mode `print`, reproduit à l'échelle les cotes du PDF (région 30 mm, liste 70→185 mm,
 * prix alignés à 185 mm) grâce aux unités mm du schéma `layout`.
 */
import { useMemo, useEffect } from 'react';
import { Wine, Martini, GlassWater, ShoppingBag } from 'lucide-react';
import { fileUrl } from '../../services/api';

const FluteIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M9 3h6l-.8 8.2a2.2 2.2 0 0 1-4.4 0L9 3Z" /><path d="M12 15v6" /><path d="M9 21h6" />
  </svg>
);
const SYMBOL_ICON = { verre: Wine, ballon: GlassWater, flute: FluteIcon, coupe: Martini };

const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const SERIF_FONTS = new Set(['Georgia', 'Times', 'Cormorant Garamond', 'Cormorant']);
const fontFamily = (f) => {
  if (!f) return undefined;
  return SERIF_FONTS.has(f) ? "'Cormorant Garamond', Georgia, serif" : "'IBM Plex Sans', system-ui, sans-serif";
};
const styleOf = (t = {}) => ({
  fontFamily: fontFamily(t.font),
  fontSize: t.size ? `${t.size}pt` : undefined,
  color: t.color || undefined,
  fontWeight: t.bold ? 700 : 400,
  fontStyle: t.italic ? 'italic' : 'normal',
});

function priceSegments(wine, prices) {
  const sym = prices?.label_mode === 'symbole';
  const stacked = prices?.layout === 'dessous';
  const vs = prices?.verre_symbol || 'verre';
  const chip = (p) => {
    if (p.kind === 'bouteille') return { icon: null, text: p.value };
    if (p.kind === 'verre') return sym ? { icon: vs, text: p.value } : { icon: null, text: `${p.label || ''} ${p.value}`.trim() };
    return sym ? { icon: 'bag', text: p.value } : { icon: null, text: `${p.label || ''} ${p.value}`.trim() };
  };
  const lines = [];
  (wine.price_years || []).forEach((py) => {
    const prefix = wine.multi && py.year ? `${py.year} – ` : '';
    const chips = py.parts.map(chip);
    if (stacked) {
      chips.forEach((c, i) => lines.push([...(i === 0 && prefix ? [{ icon: null, text: prefix }] : []), c]));
    } else {
      const line = [];
      if (prefix) line.push({ icon: null, text: prefix });
      chips.forEach((c, i) => { if (i > 0) line.push({ icon: null, text: ' · ' }); line.push(c); });
      lines.push(line);
    }
  });
  return lines;
}

function PriceIcon({ kind }) {
  const Ic = kind === 'bag' ? ShoppingBag : (SYMBOL_ICON[kind] || Wine);
  return <Ic className="inline-block mx-0.5 shrink-0" style={{ width: '0.95em', height: '0.95em' }} strokeWidth={1.75} />;
}

function WineLine({ wine, typo, labels, ruleColor, prices }) {
  const pl = priceSegments(wine, prices);
  const meta = [wine.appellation, (wine.cepages || []).filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  return (
    <div style={{ borderBottom: `0.5pt solid ${ruleColor || '#cbd5e8'}`, paddingBottom: 5, marginBottom: 5 }} data-testid={`preview-wine-${wine.cuvee_id}`}>
      <div className="flex items-start justify-between gap-3">
        {/* Colonne gauche : titre, appellation/cépages, description, accords */}
        <div className="min-w-0 flex-1">
          <div style={styleOf(typo.cuvee)}>{wine.title}</div>
          {meta && <div style={{ ...styleOf(typo.texte), color: '#6b6b6b' }}>{meta}</div>}
          {wine.description && <div style={{ ...styleOf(typo.texte), fontStyle: 'italic' }}>{wine.description}</div>}
          {(wine.accords || []).length > 0 && <div style={{ ...styleOf(typo.texte), color: '#6b6b6b' }}>{labels.accords || 'Accords :'} {wine.accords.filter(Boolean).join(', ')}</div>}
        </div>
        {/* Colonne droite : [Rupture] et/ou un prix par millésime, aligné sur le titre */}
        {(pl.length > 0 || wine.rupture) && (
          <div className="text-right shrink-0">
            {wine.rupture && (
              <div className="whitespace-nowrap" style={styleOf(typo.prix)} data-testid={`preview-rupture-${wine.cuvee_id}`}>[Rupture]</div>
            )}
            {pl.map((line, i) => (
              <div key={i} className="whitespace-nowrap flex items-center justify-end" style={styleOf(typo.prix)}>
                {line.map((seg, j) => (
                  <span key={j} className="inline-flex items-center">
                    {seg.icon && <PriceIcon kind={seg.icon} />}
                    {seg.text ? <span>{seg.text}</span> : null}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CardPreview({ content, filter = '', onCount, print = false }) {
  const typo = content.typo || {};
  const labels = content.labels || {};
  const L = content.layout || { region_x: 30, list_x: 70, right_x: 185, edge: 10 };
  const prices = content.prices || {};
  const nf = norm(filter);

  const sections = useMemo(() => {
    if (!nf) return content.sections;
    const match = (w) => [w.name, w.domain_name, w.appellation, w.region, (w.cepages || []).join(' '), w.description, (w.accords || []).join(' ')].some((f) => norm(f).includes(nf));
    return content.sections
      .map((s) => ({ ...s, subsections: s.subsections.map((sub) => ({ ...sub, wines: sub.wines.filter(match) })).filter((sub) => sub.wines.length) }))
      .filter((s) => s.subsections.length);
  }, [content.sections, nf]);

  const shown = sections.reduce((n, s) => n + s.subsections.reduce((m, sub) => m + sub.wines.length, 0), 0);
  const total = content.sections.reduce((n, s) => n + s.subsections.reduce((m, sub) => m + sub.wines.length, 0), 0);
  useEffect(() => { if (onCount) onCount(shown, total); }, [shown, total, onCount]);

  const regionStyle = styleOf(typo.region);
  const colorTitleStyle = { ...regionStyle, fontSize: `${(typo.region?.size || 13) + 5}pt` };
  const header = content.header || {};

  // Décalages en mm (mode print : aligne l'aperçu sur le PDF à l'échelle).
  const regionML = print ? `${L.region_x}mm` : 0;
  const regionW = print ? `${L.list_x - L.region_x}mm` : undefined;
  const listML = print ? `${L.list_x}mm` : 0;
  const listW = print ? `${L.right_x - L.list_x}mm` : undefined;

  const listBlock = (wines, ml) => (
    <div style={{ marginLeft: ml, width: listW }}>
      {wines.map((w) => <WineLine key={w.cuvee_id + (w.price_years?.[0]?.year || '')} wine={w} typo={typo} labels={labels} ruleColor={content.rule_color} prices={prices} />)}
    </div>
  );

  return (
    <div data-testid="card-preview">
      {/* En-tête : logo (gauche) + slogan (droite) à 10 mm des bords en mode print */}
      {print && (header.mode === 'bandeau' && header.banner_file_id ? (
        <img src={fileUrl(header.banner_file_id)} alt="" style={{ width: '210mm', height: '35mm', objectFit: 'cover', margin: `0 -${L.edge}mm` }} />
      ) : (
        <div className="flex items-start justify-between mb-4" style={{ minHeight: '22mm', paddingLeft: `${L.edge}mm`, paddingRight: `${L.edge}mm` }}>
          {header.logo_file_id ? <img src={fileUrl(header.logo_file_id)} alt="" style={{ maxHeight: '16.5mm', maxWidth: '52mm', objectFit: 'contain' }} />
            : <span className="font-title" style={{ fontSize: '14pt' }}>{content.establishment}</span>}
          {content.slogan && <span style={{ ...styleOf(typo.slogan), maxWidth: '90mm', textAlign: 'right' }}>{content.slogan}</span>}
        </div>
      ))}
      {!print && content.slogan && <p className="text-right mb-3" style={styleOf(typo.slogan)}>{content.slogan}</p>}

      {nf && shown === 0 && <p className="text-center text-n500 py-8" data-testid="preview-noresult">Aucun vin ne correspond à « {filter} ».</p>}

      {sections.map((section) => (
        <section key={section.key} style={{ marginBottom: print ? '8mm' : '1.5rem' }}>
          <h2 style={{ ...(section.color ? colorTitleStyle : regionStyle), marginLeft: regionML, marginBottom: print ? '2mm' : '0.5rem' }}>{section.label}</h2>
          {section.subsections.map((sub, i) => (
            <div key={i} style={{ marginBottom: print ? '6mm' : '0.75rem' }} className={print ? 'flex items-start' : ''}>
              {sub.region && (
                print
                  ? <h3 style={{ ...regionStyle, marginLeft: regionML, width: regionW, flexShrink: 0, paddingRight: '5px', boxSizing: 'border-box', overflowWrap: 'break-word' }}>{sub.region}</h3>
                  : <h3 className="mb-1.5" style={regionStyle}>{sub.region}</h3>
              )}
              {print ? listBlock(sub.wines, sub.region ? 0 : listML) : listBlock(sub.wines, 0)}
            </div>
          ))}
        </section>
      ))}
      {content.footer && <p className="text-center text-n500 mt-6" style={{ ...styleOf(typo.texte), fontSize: '8pt' }}>{content.footer}</p>}
    </div>
  );
}
